import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TabUnstyled from '@mui/base/TabUnstyled';
import TabsListUnstyled from '@mui/base/TabsListUnstyled';
import TabPanelUnstyled from '@mui/base/TabPanelUnstyled';
import TabsUnstyled from '@mui/base/TabsUnstyled';
import { Stack } from '@mui/material';
import * as XLSX from 'xlsx';
import './App.css';
import {
  FormattedMessage, FormattedDate, injectIntl, FormattedTime,
} from 'react-intl';
import CardBody from './components/Card';
import UsersTable from './components/UsersTable';
import UserDetails from './components/UserDetails/component';
import { UserDetailsContext } from './components/UserDetails/context';
import StatusTable from './components/StatusTable';
import PollsTable from './components/PollsTable';
import PluginsTable from './components/PluginsTable';
import ErrorMessage from './components/ErrorMessage';
import { makeUserCSVData, makeUserAOA, tsToHHmmss } from './services/UserService';
import QuizzesTable from './components/QuizzesTable';
import QuizzesChart from './components/QuizzesChart';
import {
  Modal, ModalBody, ModalContent, ModalDismissButton, ModalTitle,
} from './components/Modal';
import Help from './components/Help';

const TABS = {
  OVERVIEW: 0,
  OVERVIEW_ACTIVITY_SCORE: 1,
  TIMELINE: 2,
  POLLING: 3,
  QUIZZES: 4,
};
const LEARNING_DASHBOARD_LEARN_MORE_LINK = 'learning-dashboard-learn-more-link';
const LEARNING_DASHBOARD_FEEDBACK_LINK = 'learning-dashboard-feedback-link';

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      invalidSessionCount: 0,
      activitiesJson: {},
      tab: 0,
      meetingId: '',
      learningDashboardAccessToken: '',
      ldAccessTokenCopied: false,
      sessionToken: '',
      lastUpdated: null,
      modalOpen: false,
      exportMenuOpen: false,
    };

    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleOpenModal = this.handleOpenModal.bind(this);
  }

  componentDidMount() {
    this.setDashboardParams(() => {
      this.fetchActivitiesJson();
    });
  }

  handleOpenModal() {
    if (process.env.REACT_APP_EXTERNAL_HELP_PAGE_URL) {
      window.open(process.env.REACT_APP_EXTERNAL_HELP_PAGE_URL, '_blank');
      return;
    }
    this.setState({ modalOpen: true });
  }

  handleCloseModal() {
    this.setState({ modalOpen: false });
  }

  handleToggleExportMenu() {
    this.setState((state) => ({ exportMenuOpen: !state.exportMenuOpen }));
  }

  handleCloseExportMenu() {
    this.setState({ exportMenuOpen: false });
  }

  handleExportCSV() {
    const { intl } = this.props;
    const { activitiesJson } = this.state;
    const {
      name: meetingName, createdOn, users, polls, downloadSessionDataEnabled,
    } = activitiesJson;

    this.setState({ exportMenuOpen: false });
    if (downloadSessionDataEnabled === false) return;

    const data = makeUserCSVData(users, polls, intl);
    const filename = `LearningDashboard_${meetingName}_${new Date(createdOn).toISOString().substr(0, 10)}.csv`.replace(/ /g, '-');

    const link = document.createElement('a');
    // Prepend a UTF-8 BOM (\uFEFF) so Microsoft Excel detects UTF-8 and renders Persian/non-ASCII
    // text correctly instead of falling back to the system ANSI codepage.
    link.setAttribute('href', `data:text/csv;charset=UTF-8,${encodeURIComponent(`\uFEFF${data}`)}`);
    link.setAttribute('download', filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  handleExportExcel() {
    const { intl } = this.props;
    const { activitiesJson } = this.state;
    const {
      name: meetingName, createdOn, users, polls, downloadSessionDataEnabled,
    } = activitiesJson;

    this.setState({ exportMenuOpen: false });
    if (downloadSessionDataEnabled === false) return;

    // Build a real .xlsx so Excel opens it with correct columns/encoding directly (no import
    // wizard, no delimiter issues). Mirror the dashboard direction: RTL sheet when the meeting
    // language is right-to-left (e.g. Persian), LTR otherwise.
    const isRTL = document.documentElement.getAttribute('dir') === 'rtl';
    const worksheet = XLSX.utils.aoa_to_sheet(makeUserAOA(users, polls, intl));
    const workbook = XLSX.utils.book_new();
    workbook.Workbook = { Views: [{ RTL: isRTL }] };
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Session Data');
    const filename = `LearningDashboard_${meetingName}_${new Date(createdOn).toISOString().substr(0, 10)}.xlsx`.replace(/ /g, '-');
    XLSX.writeFile(workbook, filename);
  }

  setDashboardParams(callback) {
    let learningDashboardAccessToken = '';
    let meetingId = '';
    let sessionToken = '';

    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    if (typeof params.meeting !== 'undefined') {
      meetingId = params.meeting;
    }

    if (typeof params.sessionToken !== 'undefined') {
      sessionToken = params.sessionToken;
    }

    if (typeof params.report !== 'undefined') {
      learningDashboardAccessToken = params.report;
    } else {
      const cookieName = `ld-${params.meeting}`;
      const cDecoded = decodeURIComponent(document.cookie);
      const cArr = cDecoded.split('; ');
      cArr.forEach((val) => {
        if (val.indexOf(`${cookieName}=`) === 0) {
          learningDashboardAccessToken = val.substring((`${cookieName}=`).length);
        }
      });

      // Extend AccessToken lifetime by 7d (in each access)
      if (learningDashboardAccessToken !== '') {
        const cookieExpiresDate = new Date();
        cookieExpiresDate.setTime(cookieExpiresDate.getTime() + (3600000 * 24 * 7));
        const value = `ld-${meetingId}=${learningDashboardAccessToken};`;
        const expire = `expires=${cookieExpiresDate.toGMTString()};`;
        const args = 'path=/;SameSite=None;Secure';
        document.cookie = `${value} ${expire} ${args}`;
      }
    }

    this.setState({ learningDashboardAccessToken, meetingId, sessionToken }, () => {
      if (typeof callback === 'function') callback();
    });
  }

  getAverageActivityScore() {
    const { activitiesJson } = this.state;
    let meetingAveragePoints = 0;

    const allUsers = Object.values(activitiesJson.users || {})
      .filter((currUser) => !currUser.isModerator);
    const nrOfUsers = allUsers.length;

    if (nrOfUsers === 0) return meetingAveragePoints;

    // Calculate points of Talking
    const usersTalkTime = allUsers.map((currUser) => currUser.talk.totalTime);
    const maxTalkTime = Math.max(...usersTalkTime);
    const totalTalkTime = usersTalkTime.reduce((prev, val) => prev + val, 0);
    if (totalTalkTime > 0) {
      meetingAveragePoints += ((totalTalkTime / nrOfUsers) / maxTalkTime) * 2;
    }

    // Calculate points of Chatting
    const usersTotalOfMessages = allUsers.map((currUser) => currUser.totalOfMessages);
    const maxMessages = Math.max(...usersTotalOfMessages);
    const totalMessages = usersTotalOfMessages.reduce((prev, val) => prev + val, 0);
    if (maxMessages > 0) {
      meetingAveragePoints += ((totalMessages / nrOfUsers) / maxMessages) * 2;
    }

    // Calculate points of Raise hand
    const usersRaiseHand = allUsers.map((currUser) => currUser?.raiseHand?.length || 0);
    const maxRaiseHand = Math.max(...usersRaiseHand);
    const totalRaiseHand = usersRaiseHand.reduce((prev, val) => prev + val, 0);
    if (maxRaiseHand > 0) {
      meetingAveragePoints += ((totalRaiseHand / nrOfUsers) / maxRaiseHand) * 2;
    }

    // Calculate points of Reactions
    const usersReactions = allUsers.map((currUser) => currUser?.reactions?.length || 0);
    const maxReactions = Math.max(...usersReactions);
    const totalReactions = usersReactions.reduce((prev, val) => prev + val, 0);
    if (maxReactions > 0) {
      meetingAveragePoints += ((totalReactions / nrOfUsers) / maxReactions) * 2;
    }

    // Calculate points of Polls
    const totalOfPolls = Object.values(activitiesJson.polls || {}).length;
    if (totalOfPolls > 0) {
      const totalAnswers = allUsers
        .reduce((prevVal, currUser) => prevVal + Object.values(currUser.answers || {}).length, 0);
      meetingAveragePoints += ((totalAnswers / nrOfUsers) / totalOfPolls) * 2;
    }

    return meetingAveragePoints;
  }

  getErrorMessage() {
    const { activitiesJson, learningDashboardAccessToken, sessionToken } = this.state;
    const { intl } = this.props;
    if (learningDashboardAccessToken === '' && sessionToken === '') {
      return intl.formatMessage({ id: 'app.learningDashboard.errors.invalidToken', defaultMessage: 'Invalid session token' });
    }

    if (Object.keys(activitiesJson).length === 0 || typeof activitiesJson.name === 'undefined') {
      return intl.formatMessage({ id: 'app.learningDashboard.errors.dataUnavailable', defaultMessage: 'Data is no longer available' });
    }

    return '';
  }

  fetchMostUsedReactions() {
    const { activitiesJson } = this.state;
    if (!activitiesJson) { return []; }

    // Count each reaction
    const reactionCount = {};
    const allReactionsUsed = Object
      .values(activitiesJson.users || {})
      .map((user) => user.reactions || [])
      .flat(1);
    allReactionsUsed.forEach((reaction) => {
      if (typeof reactionCount[reaction.name] === 'undefined') {
        reactionCount[reaction.name] = 0;
      }
      reactionCount[reaction.name] += 1;
    });

    // Get the three most used
    const mostUsedReactions = Object
      .entries(reactionCount)
      .filter(([, count]) => count)
      .sort(([, countA], [, countB]) => countA - countB)
      .reverse()
      .slice(0, 3);
    return mostUsedReactions.map(([reaction]) => reaction);
  }

  updateModalUser() {
    const { user, dispatch, isOpen } = this.context;
    const { activitiesJson } = this.state;
    const { users } = activitiesJson;

    if (isOpen && users[user.userKey]) {
      dispatch({
        type: 'changeUser',
        user: users[user.userKey],
      });
    }
  }

  fetchActivitiesJson() {
    const {
      learningDashboardAccessToken, meetingId, sessionToken, invalidSessionCount,
    } = this.state;

    // adjust user sessions to be compatible with old json
    const convertUserUsessionsFormat = (activitiesJson) => {
      const newActivivies = activitiesJson;
      Object.values(newActivivies.users).forEach((user) => {
        Object.values(user.intIds).forEach((intId) => {
          if (!intId?.sessions && intId?.registeredOn) {
            const newIntId = intId;
            newIntId.sessions = [
              { registeredOn: intId.registeredOn, leftOn: intId.leftOn },
            ];
          }
        });
      });
      return newActivivies;
    };

    if (learningDashboardAccessToken !== '') {
      fetch(`${meetingId}/${learningDashboardAccessToken}/learning_dashboard_data.json`)
        .then((response) => response.json())
        .then((json) => {
          this.setState({
            activitiesJson: convertUserUsessionsFormat(json),
            loading: false,
            invalidSessionCount: 0,
            lastUpdated: Date.now(),
          });
          this.updateModalUser();
        }).catch(() => {
          this.setState({ loading: false, invalidSessionCount: invalidSessionCount + 1 });
        });
    } else if (sessionToken !== '') {
      const url = new URL('/bigbluebutton/api/learningDashboard', window.location);
      fetch(`${url}?sessionToken=${sessionToken}`, { credentials: 'include' })
        .then((response) => response.json())
        .then((json) => {
          if (json.response.returncode === 'SUCCESS') {
            const jsonData = JSON.parse(json.response.data);
            this.setState({
              activitiesJson: jsonData,
              loading: false,
              invalidSessionCount: 0,
              lastUpdated: Date.now(),
            });
            this.updateModalUser();
          } else {
            // When meeting is ended the sessionToken stop working, check for new cookies
            this.setDashboardParams();
            this.setState({ loading: false, invalidSessionCount: invalidSessionCount + 1 });
          }
        })
        .catch(() => {
          this.setState({ loading: false, invalidSessionCount: invalidSessionCount + 1 });
        });
    } else {
      this.setState({ loading: false });
    }

    setTimeout(() => {
      this.fetchActivitiesJson();
    }, 10000 * (2 ** invalidSessionCount));
  }

  totalOfReactions() {
    const { activitiesJson } = this.state;
    if (activitiesJson && activitiesJson.users) {
      return Object.values(activitiesJson.users)
        .reduce((prevVal, elem) => prevVal + elem.reactions.length, 0);
    }
    return 0;
  }

  totalOfActivity() {
    const { activitiesJson } = this.state;

    const usersTimes = Object.values(activitiesJson.users || {}).reduce((prev, user) => ([
      ...prev,
      ...Object.values(user.intIds),
    ]), []);

    const minTime = Object.values(usersTimes || {}).reduce((prevVal, elem) => {
      if (prevVal === 0 || elem.sessions[0].registeredOn < prevVal) {
        return elem.sessions[0].registeredOn;
      }
      return prevVal;
    }, 0);

    const maxTime = Object.values(usersTimes || {}).reduce((prevVal, elem) => {
      if (elem.sessions[elem.sessions.length - 1].leftOn === 0) return (new Date()).getTime();
      if (elem.sessions[elem.sessions.length - 1].leftOn > prevVal) {
        return elem.sessions[elem.sessions.length - 1].leftOn;
      }
      return prevVal;
    }, 0);

    return maxTime - minTime;
  }

  renderExportMenu() {
    const { activitiesJson, exportMenuOpen } = this.state;
    if (!(activitiesJson.downloadSessionDataEnabled || false)) return null;

    return (
      <div className="relative">
        <button
          data-test="exportSessionDataDashboard"
          type="button"
          className="flex items-center border-2 text-gray-700 border-gray-200 rounded-md px-4 py-2 bg-white focus:outline-none focus:ring ring-offset-2 focus:ring-gray-500 focus:ring-opacity-50"
          onClick={this.handleToggleExportMenu.bind(this)}
          aria-haspopup="menu"
          aria-expanded={exportMenuOpen}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          &nbsp;
          <span>
            <FormattedMessage
              id="app.learningDashboard.exportSessionDataLabel"
              defaultMessage="Export this session's data"
            />
          </span>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ltr:ml-1 rtl:mr-1">
            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
          </svg>
        </button>
        {
          exportMenuOpen ? (
            <>
              <button
                type="button"
                aria-label="close menu"
                className="fixed inset-0 z-10 cursor-default"
                onClick={this.handleCloseExportMenu.bind(this)}
              />
              <div
                className="absolute z-20 mt-1 ltr:left-0 rtl:right-0 min-w-full whitespace-nowrap bg-white border-2 border-gray-200 rounded-md shadow-lg overflow-hidden"
                role="menu"
              >
                <button
                  data-test="exportSessionDataCSV"
                  type="button"
                  role="menuitem"
                  className="block w-full ltr:text-left rtl:text-right px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                  onClick={this.handleExportCSV.bind(this)}
                >
                  <FormattedMessage
                    id="app.learningDashboard.exportCSVLabel"
                    defaultMessage="Export as CSV"
                  />
                </button>
                <button
                  data-test="exportSessionDataExcel"
                  type="button"
                  role="menuitem"
                  className="block w-full ltr:text-left rtl:text-right px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                  onClick={this.handleExportExcel.bind(this)}
                >
                  <FormattedMessage
                    id="app.learningDashboard.exportExcelLabel"
                    defaultMessage="Export as Excel"
                  />
                </button>
              </div>
            </>
          ) : null
        }
      </div>
    );
  }

  render() {
    const {
      activitiesJson, tab, loading, lastUpdated, ldAccessTokenCopied, sessionToken, modalOpen,
    } = this.state;
    const { intl } = this.props;

    const pluginUserDataCardTitle = activitiesJson?.pluginUserDataCardTitles?.[0];
    // This line generates an array of all the plugin entries of all users,
    // this might have duplicate entries:
    const pluginUserDataColumnTitleWithDuplicates = Object.values(
      activitiesJson.users || {}, // Hardcoded for now, we will add cards relative to this key.
    ).flatMap((
      user,
    ) => user.pluginUserData?.[pluginUserDataCardTitle]).filter((
      pluginUserDataListForSpecificUser,
    ) => !!(
      pluginUserDataListForSpecificUser?.columnTitle)).map((
      pluginUserDataListForSpecificUser,
    ) => pluginUserDataListForSpecificUser?.columnTitle);
    // This line will eliminate duplicates.
    const pluginUserDataColumnTitleList = [...new Set(pluginUserDataColumnTitleWithDuplicates)];

    document.title = `${intl.formatMessage({ id: 'app.learningDashboard.bigbluebuttonTitle', defaultMessage: 'Abrat Meeting' })} - ${intl.formatMessage({ id: 'app.learningDashboard.dashboardTitle', defaultMessage: 'Learning Analytics Dashboard' })} - ${activitiesJson.name}`;

    if (loading === false && this.getErrorMessage() !== '') return <ErrorMessage message={this.getErrorMessage()} />;

    const usersCount = Object.values(activitiesJson.users || {})
      .filter((u) => activitiesJson.endedOn > 0
        || Object.values(u.intIds)
          .some((intId) => intId.sessions.some((session) => session.leftOn === 0)))
      .length;

    const polls = Object.fromEntries(Object
      .values(activitiesJson.polls || {})
      .filter((p) => !p.quiz)
      .map((p) => ([p.pollId, p])));

    const quizzes = Object.fromEntries(Object
      .values(activitiesJson.polls || {})
      .filter((p) => p.quiz)
      .map((p) => ([p.pollId, p])));

    return (
      <div className="mx-10">
        <div className="flex flex-col sm:flex-row items-start justify-between pb-3">
          <h1 className="mt-3 text-2xl font-semibold flex-1 text-center">
            <FormattedMessage id="app.learningDashboard.dashboardTitle" defaultMessage="Learning Dashboard" />
            {
              ldAccessTokenCopied === true
                ? (
                  <span className="text-xs text-gray-500 font-normal ml-2">
                    <FormattedMessage id="app.learningDashboard.linkCopied" defaultMessage="Link successfully copied!" />
                  </span>
                )
                : null
            }
            <br />
            { activitiesJson?.other
              && activitiesJson.other[LEARNING_DASHBOARD_LEARN_MORE_LINK] !== ''
              && (
                <>
                  <span className="text-sm font-light font-base mt-0">
                    {intl.formatMessage({ id: 'app.learningDashboard.learnMore', defaultMessage: 'Learn more about the use of the Dashboard in {learnMoreLink} from our Knowledge Base.' }, {
                      learnMoreLink: (
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href={activitiesJson.other[LEARNING_DASHBOARD_LEARN_MORE_LINK]}
                          className="underline"
                        >
                          {intl.formatMessage({ id: 'app.learningDashboard.learnMoreLinkText', defaultMessage: 'this article' })}
                        </a>
                      ),
                    })}
                  </span>
                  <br />
                </>
              )}
            <span className="text-sm font-medium">{activitiesJson.name || ''}</span>
          </h1>
          <div className="mt-3 col-text-right py-1 text-gray-500 inline-block">
            <p className="font-bold">
              <div className="inline" data-test="meetingDateDashboard">
                <FormattedDate
                  value={activitiesJson.createdOn}
                  year="numeric"
                  month="short"
                  day="numeric"
                />
              </div>
              &nbsp;&nbsp;
              {
                activitiesJson.endedOn > 0
                  ? (
                    <span className="px-2 py-1 font-semibold leading-tight text-red-700 bg-red-100 rounded-full">
                      <FormattedMessage id="app.learningDashboard.indicators.meetingStatusEnded" defaultMessage="Ended" data-test="meetingStatusEndedDashboard" />
                    </span>
                  )
                  : null
              }
              {
                activitiesJson.endedOn === 0
                  ? (
                    <span className="px-2 py-1 font-semibold leading-tight text-green-700 bg-green-100 rounded-full" data-test="meetingStatusActiveDashboard">
                      <FormattedMessage id="app.learningDashboard.indicators.meetingStatusActive" defaultMessage="Active" />
                    </span>
                  )
                  : null
              }
            </p>
            <p data-test="meetingDurationTimeDashboard">
              <FormattedMessage id="app.learningDashboard.indicators.duration" defaultMessage="Duration" />
              :&nbsp;
              {tsToHHmmss(this.totalOfActivity())}
            </p>
          </div>
        </div>

        <TabsUnstyled
          defaultValue={0}
          onChange={(e, v) => {
            this.setState({ tab: v });
          }}
        >
          <TabsListUnstyled className="grid gap-6 mb-8 md:grid-cols-2 xl:grid-cols-4">
            <TabUnstyled className="rounded focus:outline-none focus:ring focus:ring-pink-500 ring-offset-2" data-test="activeUsersPanelDashboard">
              <Card>
                <CardContent classes={{ root: '!p-0' }}>
                  <CardBody
                    name={
                      activitiesJson.endedOn === 0
                        ? intl.formatMessage({ id: 'app.learningDashboard.indicators.usersOnline', defaultMessage: 'Active Users' })
                        : intl.formatMessage({ id: 'app.learningDashboard.indicators.usersTotal', defaultMessage: 'Total Number Of Users' })
                    }
                    number={usersCount}
                    cardClass={tab === TABS.OVERVIEW ? 'border-pink-500' : 'hover:border-pink-500 border-white'}
                    iconClass="bg-pink-50 text-pink-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </CardBody>
                </CardContent>
              </Card>
            </TabUnstyled>
            <TabUnstyled className="rounded focus:outline-none focus:ring focus:ring-green-500 ring-offset-2" data-test="activityScorePanelDashboard">
              <Card>
                <CardContent classes={{ root: '!p-0' }}>
                  <CardBody
                    name={intl.formatMessage({ id: 'app.learningDashboard.indicators.activityScore', defaultMessage: 'Activity Score' })}
                    number={intl.formatNumber((this.getAverageActivityScore() || 0), {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 1,
                    })}
                    cardClass={tab === TABS.OVERVIEW_ACTIVITY_SCORE ? 'border-green-500' : 'hover:border-green-500 border-white'}
                    iconClass="bg-green-200 text-green-700"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                      />
                    </svg>
                  </CardBody>
                </CardContent>
              </Card>
            </TabUnstyled>
            <TabUnstyled className="rounded focus:outline-none focus:ring focus:ring-purple-500 ring-offset-2" data-test="timelinePanelDashboard">
              <Card>
                <CardContent classes={{ root: '!p-0' }}>
                  <CardBody
                    name={intl.formatMessage({ id: 'app.learningDashboard.indicators.timeline', defaultMessage: 'Timeline' })}
                    number={this.totalOfReactions()}
                    cardClass={tab === TABS.TIMELINE ? 'border-purple-500' : 'hover:border-purple-500 border-white'}
                    iconClass="bg-purple-200 text-purple-500"
                  >
                    {this.fetchMostUsedReactions()}
                  </CardBody>
                </CardContent>
              </Card>
            </TabUnstyled>
            <TabUnstyled className="rounded focus:outline-none focus:ring focus:ring-blue-500 ring-offset-2" data-test="pollsPanelDashboard">
              <Card>
                <CardContent classes={{ root: '!p-0' }}>
                  <CardBody
                    name={intl.formatMessage({ id: 'app.learningDashboard.indicators.polls', defaultMessage: 'Polls' })}
                    number={Object.keys(polls).length}
                    cardClass={tab === TABS.POLLING ? 'border-blue-500' : 'hover:border-blue-500 border-white'}
                    iconClass="bg-blue-100 text-blue-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                      />
                    </svg>
                  </CardBody>
                </CardContent>
              </Card>
            </TabUnstyled>
            <TabUnstyled className="rounded focus:outline-none focus:ring focus:ring-orange-500 ring-offset-2" data-test="quizzesPanelDashboard">
              <Card>
                <CardContent classes={{ root: '!p-0' }}>
                  <CardBody
                    name={intl.formatMessage({ id: 'app.learningDashboard.indicators.quizzes', defaultMessage: 'Quizzes' })}
                    number={Object.keys(quizzes).length}
                    cardClass={tab === TABS.QUIZZES ? 'border-orange-500' : 'hover:border-orange-500 border-white'}
                    iconClass="bg-orange-100 text-orange-500"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </CardBody>
                </CardContent>
              </Card>
            </TabUnstyled>
            {pluginUserDataColumnTitleList.length && (
              <TabUnstyled className="rounded focus:outline-none focus:ring focus:ring-red-500 ring-offset-2" data-test="pluginsPanelDashboard">
                <Card>
                  <CardContent classes={{ root: '!p-0' }}>
                    <CardBody
                      name={pluginUserDataCardTitle}
                      number={pluginUserDataColumnTitleList.length}
                      cardClass={tab === TABS.POLLING ? 'border-red-500' : 'hover:border-red-500 border-white'}
                      iconClass="bg-red-100 text-red-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                        />
                      </svg>
                    </CardBody>
                  </CardContent>
                </Card>
              </TabUnstyled>
            )}
          </TabsListUnstyled>
          <TabPanelUnstyled value={0}>
            <h2 className="block my-2 pr-2 text-xl font-semibold">
              <FormattedMessage id="app.learningDashboard.usersTable.title" defaultMessage="Overview" />
            </h2>
            <div className="w-full overflow-hidden rounded-md shadow-xs border-2 border-gray-100">
              <div className="w-full overflow-x-auto">
                <UsersTable
                  allUsers={activitiesJson.users}
                  totalOfActivityTime={this.totalOfActivity()}
                  totalOfPolls={Object.values(activitiesJson.polls || {}).length}
                  tab="overview"
                />
              </div>
            </div>
          </TabPanelUnstyled>
          <TabPanelUnstyled value={1}>
            <h2 className="block my-2 pr-2 text-xl font-semibold">
              <FormattedMessage id="app.learningDashboard.usersTable.title" defaultMessage="Overview" />
            </h2>
            <div className="w-full overflow-hidden rounded-md shadow-xs border-2 border-gray-100">
              <div className="w-full overflow-x-auto">
                <UsersTable
                  allUsers={activitiesJson.users}
                  totalOfActivityTime={this.totalOfActivity()}
                  totalOfPolls={Object.values(activitiesJson.polls || {}).length}
                  tab="overview_activityscore"
                />
              </div>
            </div>
          </TabPanelUnstyled>
          <TabPanelUnstyled value={2}>
            <h2 className="block my-2 pr-2 text-xl font-semibold">
              <FormattedMessage id="app.learningDashboard.statusTimelineTable.title" defaultMessage="Timeline" />
            </h2>
            <div className="w-full overflow-hidden rounded-md shadow-xs border-2 border-gray-100">
              <div className="w-full overflow-x-auto">
                <StatusTable
                  allUsers={activitiesJson.users}
                  slides={activitiesJson.presentationSlides}
                  meetingId={activitiesJson.intId}
                  sessionToken={sessionToken}
                />
              </div>
            </div>
          </TabPanelUnstyled>
          <TabPanelUnstyled value={3}>
            <h2 className="block my-2 pr-2 text-xl font-semibold">
              <FormattedMessage id="app.learningDashboard.pollsTable.title" defaultMessage="Polls" />
            </h2>
            <div className="w-full overflow-hidden rounded-md shadow-xs border-2 border-gray-100">
              <div className="w-full overflow-x-auto">
                <PollsTable polls={polls} allUsers={activitiesJson.users} />
              </div>
            </div>
          </TabPanelUnstyled>
          <TabPanelUnstyled value={4}>
            <h2 className="block my-2 pr-2 text-xl font-semibold">
              <FormattedMessage id="app.learningDashboard.quizzes.title" defaultMessage="Quiz Results" />
            </h2>
            <Stack spacing={2}>
              <QuizzesChart
                quizzes={quizzes}
                allUsers={activitiesJson.users}
                totalOfPolls={Object.values(activitiesJson.polls || {}).length}
              />
              <QuizzesTable
                quizzes={quizzes}
                allUsers={activitiesJson.users}
              />
            </Stack>
          </TabPanelUnstyled>
          <TabPanelUnstyled value={5}>
            <h2 className="block my-2 pr-2 text-xl font-semibold">
              {pluginUserDataCardTitle}
            </h2>
            <div className="w-full overflow-hidden rounded-md shadow-xs border-2 border-gray-100">
              <div className="w-full overflow-x-auto">
                <PluginsTable
                  pluginUserDataCardTitle={pluginUserDataCardTitle}
                  pluginUserDataColumnTitleList={pluginUserDataColumnTitleList}
                  allUsers={activitiesJson.users}
                />
              </div>
            </div>
          </TabPanelUnstyled>
        </TabsUnstyled>
        <UserDetails dataJson={activitiesJson} />
        <hr className="my-8" />
        { activitiesJson?.other
          && activitiesJson.other[LEARNING_DASHBOARD_FEEDBACK_LINK] !== ''
          && (
            <>
              <div className="mt-6 mb-4 text-sm font-light font-base text-gray-500">
                { intl.formatMessage({ id: 'app.learningDashboard.feedback', defaultMessage: 'How has your experience been with this feature? We would love to hear your opinion and even suggestions on how we can improve it. Share with us by clicking {feedbackLink}.' }, {
                  feedbackLink: (
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={activitiesJson.other[LEARNING_DASHBOARD_FEEDBACK_LINK]}
                      className="underline"
                    >
                      {intl.formatMessage({ id: 'app.learningDashboard.feedbackLinkText', defaultMessage: 'here' })}
                    </a>
                  ),
                })}
              </div>
              <hr className="mb-8" />
            </>
          )}
        <div className="flex justify-between pb-8 text-xs text-gray-800 dark:text-gray-400 whitespace-nowrap flex-col sm:flex-row sm:rtl:flex-row-reverse">
          <div className="flex flex-col justify-center mb-4 sm:mb-0 ltr:text-left rtl:text-right">
            <p className="text-gray-700">
              {
                lastUpdated && (
                  <>
                    <FormattedMessage
                      id="app.learningDashboard.lastUpdatedLabel"
                      defaultMessage="Last updated at"
                    />
                    &nbsp;
                    {/* Wrap time and date in SEPARATE <bdi> (unicode-bidi: isolate) so their
                        number runs don't bleed into / reorder against each other in the RTL line
                        (the cause of the jumbled day/time/month/year). Order: time then date. */}
                    <bdi>
                      <FormattedTime
                        value={lastUpdated}
                      />
                    </bdi>
                    &nbsp;
                    <bdi>
                      <FormattedDate
                        value={lastUpdated}
                        year="numeric"
                        month="long"
                        day="numeric"
                      />
                    </bdi>
                  </>
                )
              }
            </p>
          </div>
          <div className="flex gap-2">
            {this.renderExportMenu()}
            <button
              type="button"
              className="border-2 text-gray-700 border-gray-200 rounded-md px-4 py-2 bg-white focus:outline-none focus:ring ring-offset-2 focus:ring-gray-500 focus:ring-opacity-50 flex items-center"
              onClick={this.handleOpenModal}
            >
              {process.env.REACT_APP_EXTERNAL_HELP_PAGE_URL ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
                </svg>
              )}
              &nbsp;
              <FormattedMessage
                id="app.learningDashboard.helpButtonLabel"
                defaultMessage="Help"
              />
            </button>
            <Modal isOpen={modalOpen}>
              <ModalContent>
                <ModalTitle>
                  <FormattedMessage
                    id="app.learningDashboard.help.title"
                    defaultMessage="Learning Dashboard Help"
                  />
                </ModalTitle>
                <ModalDismissButton onClick={this.handleCloseModal} />
                <ModalBody>
                  <Help />
                </ModalBody>
              </ModalContent>
            </Modal>
          </div>
        </div>
      </div>
    );
  }
}

App.contextType = UserDetailsContext;

export default injectIntl(App);
